import { useMutation, useQuery } from '@apollo/client';
import {
    getListViewProfilesTemplate,
    saveListViewProfileTemplate,
    deleteListViewProfileTemplate,
    setDefaultOrderListProfileTemplate,
} from 'apollo/templates/organization';
import { ListViewProfile, ListViewProfileInput, OrderListProfileDefaults, OrderListScope } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type UseListViewProfilesResult = {
    profiles: ListViewProfile[];
    defaults: OrderListProfileDefaults;
    loading: boolean;
    refetch: () => Promise<unknown>;
    saveProfile: (input: ListViewProfileInput) => Promise<ListViewProfile | null>;
    deleteProfile: (profileId: string) => Promise<boolean>;
    setDefaultProfile: (scope: OrderListScope, profileId: string | null) => Promise<boolean>;
    saveLoading: boolean;
    deleteLoading: boolean;
    setDefaultLoading: boolean;
};

const EMPTY_DEFAULTS: OrderListProfileDefaults = { mainProfileId: null, workflowProfileId: null };

export const useListViewProfiles = (): UseListViewProfilesResult => {
    const { showBoundary } = useDenthubErrorBoundary();

    const { data, loading, refetch } = useQuery<{
        listViewProfiles: ListViewProfile[];
        orderListProfileDefaults: OrderListProfileDefaults | null;
    }>(getListViewProfilesTemplate, {
        fetchPolicy: 'cache-and-network',
        onError: (error) => showBoundary(error),
    });

    const [saveMutation, { loading: saveLoading }] = useMutation<{ saveListViewProfile: ListViewProfile }>(
        saveListViewProfileTemplate,
        {
            refetchQueries: [{ query: getListViewProfilesTemplate }],
            onError: (error) => showBoundary(error),
        },
    );

    const [deleteMutation, { loading: deleteLoading }] = useMutation<{ deleteListViewProfile: boolean }>(
        deleteListViewProfileTemplate,
        {
            refetchQueries: [{ query: getListViewProfilesTemplate }],
            onError: (error) => showBoundary(error),
        },
    );

    const [setDefaultMutation, { loading: setDefaultLoading }] = useMutation<{
        setDefaultOrderListProfile: boolean;
    }>(setDefaultOrderListProfileTemplate, {
        refetchQueries: [{ query: getListViewProfilesTemplate }],
        onError: (error) => showBoundary(error),
    });

    const saveProfile = async (input: ListViewProfileInput) => {
        const result = await saveMutation({ variables: { profile: input } });
        return result.data?.saveListViewProfile ?? null;
    };

    const deleteProfile = async (profileId: string) => {
        const result = await deleteMutation({ variables: { profileId } });
        return Boolean(result.data?.deleteListViewProfile);
    };

    const setDefaultProfile = async (scope: OrderListScope, profileId: string | null) => {
        const result = await setDefaultMutation({ variables: { scope, profileId } });
        return Boolean(result.data?.setDefaultOrderListProfile);
    };

    return {
        profiles: data?.listViewProfiles ?? [],
        defaults: data?.orderListProfileDefaults ?? EMPTY_DEFAULTS,
        loading,
        refetch,
        saveProfile,
        deleteProfile,
        setDefaultProfile,
        saveLoading,
        deleteLoading,
        setDefaultLoading,
    };
};
